import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { TranslateService } from '@ngx-translate/core';

import { environment } from '../../../environments/environment';
import { IProductos, IPromosEcommerce } from '../../private/shared/models/productos';
import {
  EAnalyticsEvents,
  EMenuHamburguerEvents,
  EMenuMoreEvents,
  EProductsListTypes,
  ESectionProperty,
} from '../enums/analytics.enum';
import {
  IConfigEcommerceAnalytics,
  IEcommerceAnalyticsGTM,
  IFBAnalyticsEvent,
  IGTMAnalyticsEvent,
  IGTMPageViewEvent,
  IInteractionModalEvent,
  IPageViewEvent,
  IProductItem,
  IProductItemsList,
  IPromotionGTM,
  LocationIdPromos,
  TCreativeName,
  TCretiveSlot,
  TListId,
  TListNames,
  TListTypes,
  TListTypes2,
  TOriginName,
} from '../models/Analytics';
import { IAssistanceRecived } from '../models/Assistance';
import {
  AllRoutesAnalytics,
  DefaultAnalyticsRoute,
  EventInteraction,
  GTMInteractions,
  NativeStorageNamesV2,
} from '../models/Constants';
import { IUserInfoGames } from '../models/Games.model';
import { NavigationService } from './navigation/navigation.service';
import { OperationsService } from './operations/operations.service';
import { StorageData } from './utilities/storage-data.class';
import { UtilitiesService } from './utilities/utilities.service';
import { PathsEmum } from '../enums/paths.enum';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {

  constructor(
    private router: Router
    , private operationS: OperationsService
    , private utilitiesS: UtilitiesService
    , private navigationS: NavigationService
    , private translate: TranslateService
  ) { }

  // -- Metodos para enviar eventos de pantalla

  public gtmCurrentScreenUser(pageEvent: IPageViewEvent, userOptions?: any) {
    const dataEvent: IGTMPageViewEvent = {} as IGTMPageViewEvent;

    if (pageEvent && pageEvent.hasOwnProperty('event')) {
      dataEvent['event'] = pageEvent.event
    };

    if (pageEvent && pageEvent.hasOwnProperty('pagina')) {
      dataEvent['pagina'] = pageEvent.pagina
    };

    if (pageEvent && pageEvent.hasOwnProperty('titulo')) {
      dataEvent['titulo'] = pageEvent.titulo
    };

    dataEvent.entorno = environment.enviromentAnalytics

    if (userOptions) {
      const optionsKeys = Object.keys(userOptions)
      optionsKeys.forEach(option => {
        dataEvent[option] = userOptions[option]
      })
    }

    window['dataLayer'].push({ ...dataEvent });
  }


  // -- Metodos para enviar eventos en interacciones

  /**
   * Metodo que permite hacer el envio de una interaccion a Firebase Analytics y GTM
   * @param analyticEvent Datos del evento, ya sea para Firebase Analytics o GTM
   * @param fbEventTypes Listado de nombre de propiedades para el envio de interaccion a Firebase Analytics
   */
  public sendEventAnalytics(
    analyticEvent: { fbEvent?: IFBAnalyticsEvent, gtmEvent?: IGTMAnalyticsEvent },
    fbEventTypes?: string[]
  ) {
    if (analyticEvent.fbEvent) {
      const fbEvent = analyticEvent.fbEvent
      const fbInteraction = {}
      fbEventTypes.forEach(type => fbInteraction[type] = fbEvent[type])
      this.fbAnalytics(fbEvent.event, fbInteraction)
    }

    if (analyticEvent.gtmEvent) {
      this.gtmEvent(analyticEvent.gtmEvent);
    }
  }

  public gtmEvent(interactionEvent: IGTMAnalyticsEvent) {
    const dataEvent = {};

    if (interactionEvent && interactionEvent.hasOwnProperty('categoria')) {
      dataEvent['data-categoria'] = interactionEvent.categoria;
    }

    if (interactionEvent && interactionEvent.hasOwnProperty('accion')) {
      dataEvent['data-accion'] = interactionEvent.accion;
    }

    if (interactionEvent && interactionEvent.hasOwnProperty('etiqueta')) {
      dataEvent['data-etiqueta'] = interactionEvent.etiqueta;
    }

    if (interactionEvent && interactionEvent.hasOwnProperty('event')) {
      dataEvent['event'] = interactionEvent.event;
    }

    if (interactionEvent && interactionEvent.hasOwnProperty('customData')) {
      dataEvent[interactionEvent.customData.key] = interactionEvent.customData.value;
    }

    window['dataLayer'].push({ ...dataEvent });
  }

  public clearEcommerceEvents() {
    window['dataLayer'].push({ ecommerce: null });
  }

  /**
   * Metodo que permite enviar analiticas
   * @param eventType Es el tipo de evento ej: interacciones
   * @param eventData Son los datos personalizados de la interaccion
   * ej: data_categoria, data_accion, data_etiqueta
   */
  public async fbAnalytics(eventType: string, eventData: any) {
    try {
      await FirebaseAnalytics.logEvent({
        name: eventType,
        params: eventData
      });
    } catch (error) {
      return;
    }
  }


  // --- Metodos para obtener datos formateados para Analytics ---

  /**
   * Metodo para obtener pagina de Analytics mediante ruta actual
   */
  public getScreenName = async (): Promise<string> => {
    try {
      const filteredPath = Object.keys(AllRoutesAnalytics).filter(path => this.router.url.includes(path))
      return filteredPath.length > 0 ? AllRoutesAnalytics[filteredPath[0]] : DefaultAnalyticsRoute
    } catch (err) {
      return DefaultAnalyticsRoute
    }
  }

  public getEcommerceDataGTM = (config: IConfigEcommerceAnalytics): IEcommerceAnalyticsGTM => {
    const ecommerceData: IEcommerceAnalyticsGTM = {};
    let productsAnalytics = []
    if (config.products.length > 0) {
      productsAnalytics = config.products.map((product, index) => {
        const productAnalytics = {
          name: product.nombre_producto,
          id: product.id_producto,
          price: product.ClavePromo
            ? String(this.operationS.twoDecimal(product.precio))
            : String(product.precio),
          brand: product.marca,
          category: product.segmento,
          variant: product.presentacion,
          position: index + 1,
          dimension13: product.familia
        };

        if (config.typeEvent === 'view') {
          productAnalytics['list'] = config.productsConfig.listName;
        } else {
          productAnalytics['quantity'] = product.productos;
        }

        return productAnalytics
      });
    }

    if (config.typeEvent === 'interaction') {
      ecommerceData[config.eventConfig.eventName] = {
        products: productsAnalytics
      };

      if (config.productsConfig.listName) {
        ecommerceData[config.eventConfig.eventName] = {
          ...ecommerceData[config.eventConfig.eventName],
          actionField: { list: config.productsConfig.listName }
        };
      }
    } else {
      ecommerceData[config.productsConfig.productsName] = productsAnalytics;
    }

    if (config.ecommerceConfig.hasCurrency) {
      ecommerceData.currencyCode = 'MXN';
    }

    if (config.ecommerceConfig.checkOutStep) {
      ecommerceData.checkout = { actionField: { step: config.ecommerceConfig.checkOutStep, options: '' } };
    }

    return ecommerceData;
  }

  /**
   * Metodo para obtener los datos del modulo Juegos para enviar a Analytics
   * @param userGames Datos del usuario en el modulo Juegos
   */
  public getUserGamesData = (userGames: IUserInfoGames): Array<{ key: string; value: any; }> => [
    { key: 'usr_vidas', value: userGames.Vidas || 0 },
    { key: 'usr_fichas', value: userGames.Corcholatas || 0 },
    { key: 'usr_puntos', value: userGames.Puntaje || 0 }
  ];

  /**
   * Metodo para obtener los datos del modulo Centro de Asistencia para enviar a Analytics
   * @param assistance Datos de la asistencia generada
   */
  public getAssistanceData = (assistance: IAssistanceRecived): Array<{ key: string; value: any; }> => [
    { key: 'asist_asunto', value: assistance.categorie || '' },
    { key: 'asist_tipo', value: assistance.issue || '' },
    { key: 'asist_medio_contacto', value: assistance.contactType === 1 ? 'telefono' : 'correo' }
  ];

  /**
   * Metodo para enviar evento al dar click en una opcion de los menus
   * @param tabName Nombre del tab al que se le dio click
   * @param menuType Tipo de menu
   */
  public sendEventMenu = async (tabName: string, menuType: 'hamburguer' | 'more'): Promise<void> => {
    const menuOptionsAnalytics = menuType === 'hamburguer' ? EMenuHamburguerEvents : EMenuMoreEvents;
    if (menuOptionsAnalytics[tabName]) {
      const pageAnalytics = await this.getScreenName();
      const eventClick = {
        ...GTMInteractions[menuOptionsAnalytics[tabName]],
        fbEvent: {
          ...GTMInteractions[menuOptionsAnalytics[tabName]].fbEvent,
          data_accion: pageAnalytics
        },
        gtmEvent: {
          ...GTMInteractions[menuOptionsAnalytics[tabName]].gtmEvent,
          accion: `/${pageAnalytics}`
        }
      };

      this.sendEventAnalytics(eventClick, EventInteraction);
    }
  }

  /**
   * @deprecated Este método ha sido reemplazado por EcommerceAnalyticsService.mapProductToAnalyticsItem().
   * Se eliminará por completo una vez completado el despliegue del rediseño del módulo de e-commerce.
   */
  public getProductItems(products: IProductos[], listName: TListNames, listType: TListTypes | string, promotionData?: IPromotionGTM): IProductItemsList {
    return {
      items: products.map((product, index) => {
        let productItem: IProductItem = {
          item_name: product.title.toLocaleLowerCase(),
          item_id: String(product.clave_producto),
          price: product.hasOwnProperty('precio_descuento') ? this.utilitiesS.twoDecimal(product['precio_descuento']) : product.precio,
          item_brand: product.marca.toLocaleLowerCase(),
          item_category: product.segmento.toLocaleLowerCase(),
          item_variant: product.subtitle.toLocaleLowerCase(),
          quantity: product.productos || 1,
          item_packaging: product.TipoEnvase ? product.TipoEnvase : "-"
        };

        if (listType !== 'orden') {
          productItem = {
            ...productItem,
            item_category2: product.modelo === 0 ? EProductsListTypes.suggested_order
              : product.modelo === 1 ? EProductsListTypes.most_selled
                : EProductsListTypes.all_products,
            item_category3: '',
            item_category4: '',
            item_category5: '',
            index: index + 1,
          }
        }

        if (listType === 'promotion') {
          productItem = { ...productItem, ...promotionData };
        } else {
          productItem = {
            ...productItem,
            item_list_name: listName,
            item_list_id: listName,
          };
        }

        return productItem;
      })
    };
  }

  /**
   * @deprecated Este método ha sido reemplazado por EcommerceAnalyticsService.mapProductToAnalyticsItem().
   * Se eliminará por completo una vez completado el despliegue del rediseño del módulo de e-commerce.
   */
  public getPromosItems(promos: IPromosEcommerce[], listName: TListNames | string, listType: TListTypes, promotionData?: IPromotionGTM): IProductItemsList {
    return {
      items: promos.map((promo, index) => {
        let promoItem: IProductItem = {
          item_name: promo.Promocion,
          item_id: promo.ClavePromo,
          price: this.utilitiesS.twoDecimal(promo.PrecioDescuento) ?? 0,
          item_brand: 'Promociones',
          item_category: 'Promociones',
          item_variant: 'is_promotion',
          quantity: listType === 'promotion' ? 1 : promo.PaquetesCanjeados,
          item_packaging: "-"
        };

        if (listName !== 'orden') {
          promoItem = {
            ...promoItem,
            item_category2: '',
            item_category3: '',
            item_category4: '',
            item_category5: this.formatTipoPromocion(promo.TipoPromocion),
            index: index + 1,
          }
        }

        if (listType === 'promotion') {
          promoItem = { ...promoItem, ...promotionData };
        } else {
          promoItem = {
            ...promoItem,
            item_list_name: listName,
            item_list_id: "promociones",
          };
        }

        return promoItem;
      })
    };
  }

  // -- DEPRECAR --

  public gtmCustomEvent(event: string, eventName: string, dataEvent: any, categoria?: string, accion?: string, etiqueta?: string) {
    const data = {
      'event': event,
      [eventName]: dataEvent
    }
    if (categoria || accion || etiqueta) {
      data['data-categoria'] = categoria
      data['data-accion'] = accion
      data['data-etiqueta'] = etiqueta
    }
    window['dataLayer'].push({ ...data })
  }

  // -- DEPRECAR --

  // ** V2 para envio de eventos GTM

  /**
   * Sends an event to Google Tag Manager (GTM).
   *
   * @param event - The event data object to be sent to GTM
   * @param options - Optional configuration for the GTM event
   * @param options.addUserProps - If true, adds encrypted user data (phone number, user_id, usr_id) to the event
   * @param options.addEnvironmentProps - If true, adds environment and platform origin information to the event
   * @returns A Promise that resolves when the event is processed
   *
   * @example
   * await analyticsService.sendEventGTM({ event_name: 'button_click' }, { addUserProps: true });
   */
  public async sendEventGTM(event: object, options?: IGTMEventOptions) {
    let dataEvent = {
      ...event
    }

    if (options && options.addUserProps) {
      const userData: IUserProfile = await StorageData.getDataStorage(NativeStorageNamesV2.PerfilUsuario)
      dataEvent = {
        ...dataEvent,
        user_id: userData.usr_idTendero,
        usr_id: userData.usr_id,
      }
    }

    if (options && options.addEnvironmentProps) {
      const platformOrigin = await StorageData.getDataStorage(NativeStorageNamesV2.PlatformOrigin);
      dataEvent = {
        ...dataEvent,
        environment: environment.enviromentAnalytics.toLocaleLowerCase(),
        origin: platformOrigin,
      }
    }

    if (options && options.addPageViewProps) {
      dataEvent = {
        ...dataEvent,
        page_location: this.navigationS.getCurrentUrl(),
        page_referrer: this.navigationS.getPreviousUrl(),
      };
    }

    if(options && options.addContextProps) {
      dataEvent = {
        ...dataEvent,
        section: PathsEmum[this.navigationS.getCurrentUrl()]
      }
    }

    window['dataLayer'].push({ ...dataEvent });
  }

  /**
   * @deprecated Este método ha sido reemplazado por EcommerceAnalyticsService.mapProductToAnalyticsItem().
   * Se eliminará por completo una vez completado el despliegue del rediseño del módulo de e-commerce.
   */
  public getProductItemsV2(products: IProductos[], itemListId: TListId, itemListName: string): IProductItemsList {
    return {
      items: products.map((product, index) => {

        let productItem: IProductItem = {
          item_name: product.title.toLocaleLowerCase(),
          item_id: String(product.clave_producto),
          price: product.hasOwnProperty('precio_descuento') ? this.utilitiesS.twoDecimal(product['precio_descuento']) : product.precio,
          item_brand: product.marca.toLocaleLowerCase(),
          item_category: product.familia.toLocaleLowerCase(),
          item_variant: product.subtitle.toLocaleLowerCase(),
          quantity: product.productos || 1,
          item_category2: product.segmento?.toLocaleLowerCase(),
          item_category3: `${product.marca?.toLocaleLowerCase()}::${product.sku?.toLocaleLowerCase()}`,
          item_category4: `${product.presentacion}::${product.TipoEnvase}::${product.Retornabilidad}`,
          item_category5: '',
          index: index + 1,
          item_list_id: itemListId,
          item_list_name: itemListName,
          item_packaging: product.TipoEnvase ? product.TipoEnvase : "-"
        };

        return productItem;
      })
    };
  }

  /**
   * @deprecated Este método ha sido reemplazado por EcommerceAnalyticsService.mapProductToAnalyticsItem().
   * Se eliminará por completo una vez completado el despliegue del rediseño del módulo de e-commerce.
   */
  public getPromosItemsV2(promos: IPromosEcommerce[], itemListId: string, itemListName: string): IProductItemsList {
    return {
      items: promos.map((promo, index) => {
        let promoItem: IProductItem = {
          item_name: promo.Promocion,
          item_id: promo.ClavePromo,
          price: this.utilitiesS.twoDecimal(promo.PrecioDescuento) ?? 0,
          item_brand: 'Promociones',
          item_category: '',
          item_category2: '',
          item_category3: '',
          item_category4: '',
          item_category5: this.formatTipoPromocion(promo.TipoPromocion),
          item_variant: 'is_promotion',
          quantity: promo.PaquetesCanjeados,
          item_list_id: itemListId,
          item_list_name: itemListName,
          index: index + 1,
          item_packaging: "-"
        };
        return promoItem;
      })
    };
  }

  public getProductItemsPromo(promo: IPromosEcommerce, canjeActual: number, itemListId: string, itemListName: string, creativeName: TCreativeName, creativeSlot: TCretiveSlot): IProductItemsList {
    const itemsPromo = promo.Productos.map((product, index) => {
      return {
        item_name: product.descripcion?.toLocaleLowerCase() || product.title?.toLocaleLowerCase(),
        item_id: String(product.clave_producto),
        price: this.utilitiesS.twoDecimal(product.precio),
        item_brand: product.marca?.toLocaleLowerCase(),
        item_category: product.familia?.toLocaleLowerCase(),
        item_category2: product.segmento?.toLocaleLowerCase(),
        item_category3: `${product.marca?.toLocaleLowerCase()}::${product.sku?.toLocaleLowerCase()}`,
        item_category4: `${product.presentacion}::${product.TipoEnvase}::${product.Retornabilidad}`,
        item_category5: this.formatTipoPromocion(promo.TipoPromocion),
        item_variant: product.subtitle.toLocaleLowerCase(),
        quantity: promo.PaquetesCanjeados === 0 ? product.productos : promo.PaquetesCanjeados,
        promotion_quantity: canjeActual,
        index: index + 1,
        item_list_id: itemListId,
        item_list_name: itemListName,
        promotion_id: promo.ClavePromo,
        promotion_name: promo.Promocion,
        discount: this.utilitiesS.twoDecimal(product.precio_original - product.precio),
        creative_name: creativeName,
        creative_slot: creativeSlot,
        item_packaging: product.TipoEnvase ? product.TipoEnvase : "-"

      }
    })
    return { items: itemsPromo }
  }

  public getlAllItemsPromoAgregadosOEliminados = (promosItems: IProductItemsList, promo: IPromosEcommerce, prevCartPromoProducts: IProductos[], prevExchange, itemListName: string) => {
    const prevPromoItems = this.getProductItemsPromo({ ...promo, Productos: prevCartPromoProducts, PaquetesCanjeados: 0 }, prevExchange, "promociones", itemListName, "Carrito", "carrito")
    // Si en los promoItems hay un producto con item_id que no estaba en prevPromoItems, entonces se esta agregando, 
    // si ya existia el mismo item_id en prevPromoItems, entonces se esta actualizando, si la cantidad es mas baja se esta eliminando y si la cantidad es mas alta se esta agregando. 
    // Si en prevPromoItems hay un item_id que no esta en promosItems, entonces se esta eliminando. Si hay un item_id que esta en ambos, entonces se esta actualizando. 
    const addedItems = promosItems.items.filter(item => !prevPromoItems.items.some(prevItem => prevItem.item_id === item.item_id && prevItem.index === item.index));
    const removedItems = prevPromoItems.items.filter(item => !promosItems.items.some(newItem => newItem.item_id === item.item_id && newItem.index === item.index));
    const updatedItems = promosItems.items.filter(item => prevPromoItems.items.some(prevItem => prevItem.item_id === item.item_id && prevItem.index === item.index && prevItem.quantity !== item.quantity));
    //por cada item actualizado, si la cantidad antes era mayor que la de ahora ese item se agrega en addedItems, si la cantidad antes era menor que la de ahora ese item se agrega en removedItems.
    //lo que se agrega o elimina es la diferencia en la cantidad de los items actualizados


    addedItems.push(...updatedItems.filter(item => prevPromoItems.items
      .find(prevItem => prevItem.item_id === item.item_id && prevItem.index === item.index).quantity < item.quantity)
      .map(item => {
        const prevItem = prevPromoItems.items.find(p => p.item_id === item.item_id && p.index === item.index);
        return {
          ...item,
          quantity: item.quantity - prevItem.quantity
        };
      })
    );
    removedItems.push(...updatedItems.filter(item => prevPromoItems.items
      .find(prevItem => prevItem.item_id === item.item_id && prevItem.index === item.index).quantity > item.quantity)
      .map(item => {
        const prevItem = prevPromoItems.items.find(p => p.item_id === item.item_id && p.index === item.index);
        return {
          ...item,
          quantity: prevItem.quantity - item.quantity
        };
      })
    );

    return [addedItems, removedItems]
  }

  public getSinglePromoAsList(promo: IPromosEcommerce, creativeName: TCreativeName, creativeSlot: TCretiveSlot, locationId: LocationIdPromos, index: number): IProductItem[] {
    let promoItem: IProductItem = {
      item_id: String(promo.ID_Promocion),
      promotion_id: promo.ClavePromo,
      price: this.utilitiesS.twoDecimal(promo.PrecioDescuento) ?? 0,
      promotion_name: promo.Promocion,
      creative_name: creativeName,
      creative_slot: creativeSlot,
      item_category: 'Promociones',
      item_category2: '',
      item_category3: '',
      item_category4: '',
      item_category5: this.formatTipoPromocion(promo.TipoPromocion),
      item_variant: 'is_promotion',
      quantity: promo.Productos.length,
      prior_exchange: promo.PaquetesCanjeados,
      index: index + 1,
      location_id: locationId,
      item_packaging: "-"
    }
    let promoItemList: IProductItem[] = [];
    promoItemList.push(promoItem);
    return promoItemList;
  }

  public getViewPromosItems(promos: IPromosEcommerce[], itemListId: string, itemListName: string, creativeName: string, creativeSlot: string, locationId?: LocationIdPromos): IProductItem[] {
    return promos.map((promo, index) => {
      let promoItem: IProductItem = {
        item_id: String(promo.ID_Promocion),
        promotion_id: promo.ClavePromo,
        price: this.utilitiesS.twoDecimal(promo.PrecioDescuento) ?? 0,
        promotion_name: promo.Promocion,
        creative_name: creativeName,
        creative_slot: creativeSlot,
        item_category: 'Promociones',
        item_category2: '',
        item_category3: '',
        item_category4: '',
        item_category5: this.formatTipoPromocion(promo.TipoPromocion),
        item_variant: 'is_promotion',
        quantity: promo.Productos.length,
        prior_exchange: promo.PaquetesCanjeados,
        index: index + 1,
        //si existe locationId se usa ese, sino debe tener el valor de index. Si el index es 0 es Top, si el index es el ultimo es Bot, sino es Middle
        location_id: locationId ? locationId : (index === 0 ? 'Top' : (index === promos.length - 1 ? 'Bot' : 'Middle')),
        item_packaging: "-"
      };
      return promoItem;
    });
  }

  public getListName(origen: TOriginName, tipo: TListTypes2, detalle: string) {
    return `${origen}::${tipo}::${detalle.trim().toLowerCase().replace(/\s+/g, '_')}`
  }

  /**
   * @deprecated Este método ha sido reemplazado por EcommerceAnalyticsService.sendViewItemList().
   * Se eliminará por completo una vez completado el despliegue del rediseño del módulo de e-commerce.
   */
  public sendEventViewItemListGTM(products: IProductos[], listId: TListId, listOriginName: TOriginName, listType: TListTypes2, detalle: string, section: ESectionProperty, eventOptions?: IGTMEventOptions, promo?: IPromosEcommerce) {
    const listName = this.getListName(listOriginName, listType, detalle);
    const analyticsItems = this.getProductItemsV2(products, listId, listName);
    if (promo) {
      analyticsItems.items = analyticsItems.items.map(item => ({ ...item, promotion_id: promo.ClavePromo, promotion_name: promo.Promocion, item_category5: this.formatTipoPromocion(promo.TipoPromocion) }));
    }
    const allItems = [...analyticsItems.items]
    const event = {
      ...EAnalyticsEvents.viewItemListEvent,
      ecommerce: {
        item_list_id: listId,
        item_list_name: listName,
        section,
        items: allItems
      }
    };

    this.sendEventGTM(event, eventOptions)
  }

  public sendEventViewModal(event: any) {
    const currentLang = this.translate.currentLang || this.translate.defaultLang || 'es';

    const modalEvent = {
      ...event,
      modal_variant: "-",
      campaign_id: '-',
      experiment_id: '-',
      experiment_variant: '-',
      language: currentLang,
      currency: '-',
    }
    this.sendEventGTM(modalEvent, { addEnvironmentProps: true });
  }

  public sendEventInteractionModal(event: IInteractionModalEvent) {
    const modalEvent = {
      ...event,
    }
    this.sendEventGTM(modalEvent, { addEnvironmentProps: true });
  }

  private formatTipoPromocion(tipo: string): string {
    return tipo ? tipo.charAt(0).toUpperCase() + tipo.slice(1).toLowerCase() : '';
  }

  public normalizeText = (text: string) => text.normalize('NFD').replace(/[\u00C0-\u00FF]/g, '').replace(/[^a-zA-Z0-9 ]/g, '').trim().replace(/\s+/g, '_');

  /**
   * Sends a mega_menu event when user selects a menu section.
   * @param element Visible menu item name (e.g., 'Mi perfil')
   * @param targetUrl Target route (e.g., '/Perfil')
   */
  public sendMegaMenuEvent = async (
    element: string,
    targetUrl: string
  ): Promise<void> => {
    const currentSection = await this.getScreenName();
    const event = {
      ...EAnalyticsEvents.megaMenuEvent,
      section: currentSection,
      element,
      url: targetUrl
    };
    await this.sendEventGTM(event, { addEnvironmentProps: true });
  }
}


interface IGTMEventOptions {
  addUserProps?: boolean;
  addEnvironmentProps?: boolean;
  addPageViewProps?: boolean;
  addContextProps?: boolean;
}

interface IUserProfile {
  usr_genero: string;
  usr_edad: number;
  usr_region: string;
  usr_id: string;
  usr_bodega: number;
  usr_programa: string;
  usr_nivel: string;
  usr_territorio: string;
  usr_ruta: string;
  usr_canal: string;
  usr_idTendero: number;
  usr_nud: number;
}